import React from 'react';
import './TermsOfService.scss';

const TermsOfService: React.FC = () => {
  const lastUpdated = 'December 2024';

  return (
    <div className="terms-of-service-container">
      <div className="content-wrapper">
        <div className="header-section">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: {lastUpdated}</p>
        </div>

        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing and using the website and services of Florida First Psychiatry ("we," "our," or "us"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
          </section>

          <section className="terms-section">
            <h2>2. Description of Service</h2>
            <p>Florida First Psychiatry provides comprehensive psychiatric evaluation and mental health treatment services for adults. Our website serves as an informational platform and may include features such as:</p>
            <ul>
              <li>Information about our psychiatric services and treatment options</li>
              <li>Appointment scheduling and patient portal access</li>
              <li>Patient forms and educational resources</li>
              <li>Information about our providers and treatment approaches</li>
              <li>Contact and communication tools</li>
              <li>Insurance and billing information</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. Medical Information Disclaimer</h2>
            <p><strong>Important:</strong> The information provided on this website is for general informational and educational purposes only. It is not intended as a substitute for professional medical advice, diagnosis, or treatment.</p>
            <ul>
              <li>Always seek the advice of a qualified healthcare provider with any questions you may have regarding a medical condition</li>
              <li>Never disregard professional medical advice or delay in seeking it because of something you have read on this website</li>
              <li>If you think you may have a medical emergency, call your doctor or 911 immediately</li>
              <li>Information on this website may not be complete, current, or applicable to your specific situation</li>
              <li>Treatment outcomes may vary and are not guaranteed</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>4. User Responsibilities</h2>
            <p>By using our services, you agree to:</p>
            <ul>
              <li>Provide accurate and complete information when requested</li>
              <li>Maintain the confidentiality of your account information</li>
              <li>Use the website and services for lawful purposes only</li>
              <li>Not attempt to gain unauthorized access to our systems</li>
              <li>Not interfere with the proper functioning of our website</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Privacy and Data Protection</h2>
            <p>Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy. By using our services, you consent to our collection and use of information as described in our privacy policy.</p>
          </section>

          <section className="terms-section">
            <h2>6. Intellectual Property Rights</h2>
            <p>All content on this website, including but not limited to text, graphics, images, logos, and software, is the property of Florida First Psychiatry or its licensors and is protected by copyright and other intellectual property laws.</p>
            <p>You may not reproduce, distribute, modify, or create derivative works from this content without our express written consent.</p>
          </section>

          <section className="terms-section">
            <h2>7. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, Florida First Psychiatry shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:</p>
            <ul>
              <li>Loss of profits, data, or use</li>
              <li>Business interruption</li>
              <li>Personal injury or property damage</li>
              <li>Any damages resulting from the use or inability to use our services</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>8. Services and Treatment</h2>
            <h3>8.1 Service Availability</h3>
            <p>Service availability is subject to change without notice. We reserve the right to modify or discontinue services at any time. You will be notified of any changes to your scheduled appointments or treatment plan.</p>
            
            <h3>8.2 Fees and Payment</h3>
            <p>All fees are subject to change without notice. Payment terms and conditions will be specified at the time of service. We accept various forms of payment and work with most insurance plans. We reserve the right to modify fees as necessary. You are responsible for any co-payments, deductibles, or fees not covered by your insurance.</p>
            
            <h3>8.3 Treatment and Outcomes</h3>
            <p>Treatment plans are individualized and based on clinical assessment. We do not guarantee specific treatment outcomes. Treatment effectiveness may vary based on individual circumstances, adherence to treatment recommendations, and other factors. You are responsible for following your treatment plan and communicating with your provider about any concerns or changes in your condition.</p>
            
            <h3>8.4 Cancellation and No-Show Policy</h3>
            <p>We require advance notice for appointment cancellations. Failure to provide adequate notice or failure to show for scheduled appointments may result in fees as specified in our appointment policies.</p>
          </section>

          <section className="terms-section">
            <h2>9. Website Availability</h2>
            <p>We strive to maintain website availability but do not guarantee uninterrupted access. We may temporarily suspend or restrict access for maintenance, updates, or other reasons. We are not liable for any damages resulting from website unavailability.</p>
          </section>

          <section className="terms-section">
            <h2>10. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites. We do not endorse or control these external sites and are not responsible for their content, privacy policies, or practices. Your use of third-party sites is at your own risk.</p>
          </section>

          <section className="terms-section">
            <h2>11. Indemnification</h2>
            <p>You agree to indemnify and hold harmless Florida First Psychiatry, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of our services or violation of these terms, except to the extent such claims arise from our negligence or willful misconduct.</p>
          </section>

          <section className="terms-section">
            <h2>12. Governing Law</h2>
            <p>These terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising from these terms shall be resolved in the courts of Leon County, Florida.</p>
          </section>

          <section className="terms-section">
            <h2>13. Changes to Terms</h2>
            <p>We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after changes constitutes acceptance of the modified terms.</p>
          </section>

          <section className="terms-section">
            <h2>14. Severability</h2>
            <p>If any provision of these terms is found to be unenforceable or invalid, the remaining provisions will continue in full force and effect.</p>
          </section>

          <section className="terms-section">
            <h2>15. Contact Information</h2>
            <p>If you have any questions about these Terms of Service, please contact us:</p>
            <div className="contact-info">
              <p><strong>Florida First Psychiatry</strong></p>
              <p>1820 Miccosukee Commons Drive<br />Tallahassee, FL 32308</p>
              <p>Phone: (850) 765-8120</p>
              <p>Email: info@ffps.pro</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

